import { ScoutPrivateDocumentExtractionStatus } from "generated-graphql/graphql";

import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";

import { isTextExtractionDocument } from "../hooks/scoutPrivateDocumentQuery";

/** Proposal kind for large-file full-analysis opt-in (domain-status source). */
export const LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND = "large_file_full_analysis";

export type ScoutLargeFileFullAnalysisProposalItem = {
  id: string;
  label: string;
  filename: string;
  sizeBytes?: number;
  pageCount?: number | null;
  previewSummary?: string | null;
};

/**
 * Oak view model for awaiting full-analysis confirmation. Mapped from
 * `PrivateDocument.extractionStatus = AWAITING_CONFIRMATION` rows — not from
 * a tool result. UI stays in composer chrome; this shape feeds the shared
 * resolve/continue protocol.
 */
export type ScoutLargeFileFullAnalysisProposal = {
  proposalId: string;
  documents: ScoutLargeFileFullAnalysisProposalItem[];
};

/**
 * Stable proposal id for a batch of awaiting documents. Single-doc batches
 * use the privateDocumentId alone; multi-doc batches join sorted ids so the
 * same set round-trips to the same synthetic-event proposalId.
 */
export function buildLargeFileFullAnalysisProposalId(privateDocumentIds: string[]): string {
  const uniqueSorted = [...new Set(privateDocumentIds.filter((id) => id.trim().length > 0))].sort();
  return uniqueSorted.join("+");
}

function toProposalItem(
  document: ScoutConversationPrivateDocumentRow
): ScoutLargeFileFullAnalysisProposalItem {
  return {
    id: document.id,
    label: document.filename,
    filename: document.filename,
    sizeBytes: document.sizeBytes,
    pageCount: document.pageCount,
    previewSummary: document.previewSummary,
  };
}

/**
 * Map conversation private-document rows with
 * `extractionStatus = AWAITING_CONFIRMATION` into one batch proposal.
 * Returns null when nothing is awaiting.
 */
export function mapAwaitingConfirmationDocumentsToLargeFileProposal(
  documents: ReadonlyArray<ScoutConversationPrivateDocumentRow>
): ScoutLargeFileFullAnalysisProposal | null {
  const awaiting = documents.filter(
    (document) =>
      isTextExtractionDocument(document) &&
      document.extractionStatus === ScoutPrivateDocumentExtractionStatus.AwaitingConfirmation
  );
  if (awaiting.length === 0) {
    return null;
  }

  const proposalDocuments = awaiting.map(toProposalItem);
  return {
    proposalId: buildLargeFileFullAnalysisProposalId(proposalDocuments.map((doc) => doc.id)),
    documents: proposalDocuments,
  };
}
